import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import Navigation from '../components/Navigation'
import RecordDetailDialog from '../components/RecordDetailDialog'
import { get } from '../utils/apiClient'
import { isHrEnabled } from '../utils/hr'
import * as XLSX from 'xlsx'

interface HistoryRecord {
  id: string
  employeeName: string
  employeeId: string
  city: string
  cityName: string
  startDate: string
  endDate: string
  totalDays: number
  companyCompensation: number
  employeeCompensation?: number
  calculatedAt: string
  recordType?: string
  receivedTime?: string // 政府补贴发放时间（年-月-日格式）
  submissionTime?: string // 提交核定表时间（年-月-日格式）
  vacationData: {
    birthType?: string
    hasExtendedDays?: boolean
    rewardLeave?: boolean
    deliveryMethod?: string
    pregnancyDuration?: number
    firstChild?: 'yes' | 'no' // 绍兴一孩选项（用户输入）
    isFirstTimeBirth?: boolean // 绍兴一孩结果（后端返回）
  }
  allowanceData: {
    monthlyAverageSalary?: number
    socialInsuranceBase?: number
    governmentSubsidy?: number
    [key: string]: unknown
  }
}

const HistoryRecords: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCity, setSelectedCity] = useState('')
  const [records, setRecords] = useState<HistoryRecord[]>([])
  const [filteredRecords, setFilteredRecords] = useState<HistoryRecord[]>([])
  const [cities, setCities] = useState<Array<{value: string, label: string}>>([])
  
  // 需要显示员工需返还金额的城市 (使用大写城市代码匹配实际数据格式)
  const employeeCompensationCities = ['TJ', 'SX', 'XM', 'SH', 'QD']

  // 城市选项
  const cityOptions = [
    { value: '', label: '全部城市' },
    ...cities.map(city => ({ value: city.value, label: city.label }))
  ]

  // 弹窗状态管理
  const [selectedRecord, setSelectedRecord] = useState<HistoryRecord | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set())
  const [selectAll, setSelectAll] = useState(false)
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10) // 每页显示10条
  
  // 排序状态
  const [sortField, setSortField] = useState<'calculatedAt' | 'receivedTime' | 'submissionTime'>('calculatedAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc') // 默认按时间倒序

  // 从MS API获取城市列表
  const loadCities = async () => {
    try {
      const response = await get<Array<{code: string, chineseName: string}>>('https://www.54cpa.cn/api/support/cities')
      const cityOptions = response.map(city => ({
        value: city.code, // 使用大写代码如 'SH'
        label: city.chineseName // 使用中文名称如 '上海'
      }))
      setCities(cityOptions)
    } catch (error) {
      console.error('获取城市列表失败:', error)
      setCities([]) // 失败时使用空数组
    }
  }

  const loadHistoryRecords = React.useCallback(() => {
    try {
      console.log('🔍 开始加载历史记录...')
      
      // 检查所有可能的localStorage键名
      const keys = ['hr_maternity_history', 'allowanceHistory', 'maternityHistoryRecords']
      let allRecords: HistoryRecord[] = []
      
      keys.forEach(key => {
        const savedRecords = localStorage.getItem(key)
        if (savedRecords) {
          console.log(`📦 从${key}加载的原始数据长度:`, savedRecords.length)
          console.log(`📦 从${key}加载的数据:`, savedRecords.substring(0, 200) + '...')
          try {
            const parsedRecords = JSON.parse(savedRecords)
            if (Array.isArray(parsedRecords)) {
              console.log(`✅ ${key}解析成功，记录数量:`, parsedRecords.length)
              allRecords = [...allRecords, ...parsedRecords]
              // 显示每条记录的基本信息
              parsedRecords.forEach((record: HistoryRecord, index: number) => {
                console.log(`记录${index + 1}: 员工=${record.employeeName}, 工号=${record.employeeId}, 城市=${record.city}, 时间=${record.calculatedAt}`)
              })
            } else {
              console.warn(`⚠️ ${key}不是数组格式:`, typeof parsedRecords)
            }
          } catch (parseError) {
            console.error(`❌ 解析${key}数据失败:`, parseError)
          }
        } else {
          console.log(`📭 ${key}为空`)
        }
      })
      
      console.log('📋 合并后的所有记录数量:', allRecords.length)
      console.log('📋 合并后的记录详情:', allRecords)
      setRecords(allRecords)
    } catch (error) {
      console.error('❌ 加载历史记录失败:', error)
    }
  }, [])

  const filterRecords = React.useCallback(() => {
    let filtered = records

    // 按姓名或工号筛选
    if (searchTerm) {
      filtered = filtered.filter(record => 
        record.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // 按城市筛选
    if (selectedCity) {
      filtered = filtered.filter(record => record.cityName === selectedCity)
    }


    // 按指定字段排序
    filtered.sort((a, b) => {
      let valueA: string | number
      let valueB: string | number
      
      if (sortField === 'calculatedAt') {
        valueA = new Date(a.calculatedAt).getTime()
        valueB = new Date(b.calculatedAt).getTime()
      } else if (sortField === 'receivedTime') {
        // 处理政府补贴发放时间排序，空值排在最后
        valueA = a.receivedTime ? new Date(a.receivedTime).getTime() : 0
        valueB = b.receivedTime ? new Date(b.receivedTime).getTime() : 0
      } else if (sortField === 'submissionTime') {
        // 处理提交核定表时间排序，空值排在最后
        valueA = a.submissionTime ? new Date(a.submissionTime).getTime() : 0
        valueB = b.submissionTime ? new Date(b.submissionTime).getTime() : 0
      } else {
        valueA = 0
        valueB = 0
      }
      
      // 空值处理：空值始终排在最后
      if (valueA === 0 && valueB === 0) return 0
      if (valueA === 0) return 1
      if (valueB === 0) return -1
      
      return sortOrder === 'desc' ? valueB - valueA : valueA - valueB
    })

    console.log('🔍 筛选结果:', filtered)
    setFilteredRecords(filtered)
    
    // 重置到第一页
    setCurrentPage(1)
  }, [records, searchTerm, selectedCity, sortField, sortOrder])

  const handleRecordClick = (record: HistoryRecord) => {
    // 检查是否有津贴计算数据
    const hasAllowanceData = record.recordType === 'allowance' && 
      record.allowanceData && 
      (record.allowanceData.monthlyAverageSalary || record.allowanceData.governmentSubsidy);

    if (hasAllowanceData) {
      // 有津贴数据，跳转到津贴计算页面
      navigate('/allowance', {
        state: {
          maternityResult: { 
            totalDays: record.totalDays,
            isFirstTimeBirth: record.vacationData?.isFirstTimeBirth // 绍兴一孩结果
          },
          formData: {
            employeeName: record.employeeName,
            lanId: record.employeeId,
            cityCode: getCityCodeByName(record.cityName || record.city),
            expectedDeliveryDate: record.startDate,
            birthType: record.vacationData?.birthType || 'single',
            hasExtendedDays: record.vacationData?.hasExtendedDays || false,
            rewardLeave: record.vacationData?.rewardLeave || false,
            deliveryMethod: record.vacationData?.deliveryMethod || 'normal',
            pregnancyDuration: record.vacationData?.pregnancyDuration,
            maternityLeaveStartDate: record.startDate,
            maternityLeaveEndDate: record.endDate,
            firstChild: record.vacationData?.firstChild // 绍兴一孩选项
          },
          allowanceData: record.allowanceData,
          fromHistory: true
        }
      })
    } else {
      // 没有津贴数据，跳转到假期计算页面
      navigate('/maternity', {
        state: {
          formData: {
            employeeName: record.employeeName,
            lanId: record.employeeId,
            cityCode: getCityCodeByName(record.cityName || record.city),
            expectedDeliveryDate: record.startDate,
            birthType: record.vacationData?.birthType || 'single',
            hasExtendedDays: record.vacationData?.hasExtendedDays || false,
            rewardLeave: record.vacationData?.rewardLeave || false,
            deliveryMethod: record.vacationData?.deliveryMethod || 'normal',
            pregnancyDuration: record.vacationData?.pregnancyDuration,
            firstChild: record.vacationData?.firstChild // 绍兴一孩选项
          },
          fromHistory: true
        }
      })
    }
  }

  const handleViewDetails = (record: HistoryRecord) => {
    // 打开弹窗显示详情
    setSelectedRecord(record)
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setSelectedRecord(null)
  }

  // 复选框处理函数
  const handleSelectRecord = (recordId: string) => {
    const newSelectedRecords = new Set(selectedRecords)
    if (newSelectedRecords.has(recordId)) {
      newSelectedRecords.delete(recordId)
    } else {
      newSelectedRecords.add(recordId)
    }
    setSelectedRecords(newSelectedRecords)
    
    // 检查当前页是否全选
    const currentPageIds = currentPageRecords.map(r => r.id)
    const currentPageAllSelected = currentPageIds.length > 0 && 
      currentPageIds.every(id => newSelectedRecords.has(id))
    setSelectAll(currentPageAllSelected)
  }

  const handleSelectAll = () => {
    const newSelectedRecords = new Set(selectedRecords)
    const currentPageIds = currentPageRecords.map(r => r.id)
    
    if (selectAll) {
      // 取消当前页的选中
      currentPageIds.forEach(id => newSelectedRecords.delete(id))
      setSelectAll(false)
    } else {
      // 选中当前页
      currentPageIds.forEach(id => newSelectedRecords.add(id))
      setSelectAll(true)
    }
    
    setSelectedRecords(newSelectedRecords)
  }

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRecords.size === 0) return
    
    if (confirm(`确定要删除选中的 ${selectedRecords.size} 条记录吗？此操作不可恢复。`)) {
      try {
        // 从 localStorage 获取现有记录
        const existingRecords: HistoryRecord[] = JSON.parse(localStorage.getItem('hr_maternity_history') || '[]')
        
        // 过滤掉选中的记录
        const updatedRecords = existingRecords.filter(record => !selectedRecords.has(record.id))
        
        // 更新 localStorage
        localStorage.setItem('hr_maternity_history', JSON.stringify(updatedRecords))
        
        // 重新加载数据
        loadHistoryRecords()
        
        // 清空选中状态
        setSelectedRecords(new Set())
        setSelectAll(false)
        
        console.log(`成功删除 ${selectedRecords.size} 条记录`)
      } catch (error) {
        console.error('删除记录时出错:', error)
        alert('删除记录时出错，请重试')
      }
    }
  }

  // 批量导出
  const handleBatchExport = () => {
    if (selectedRecords.size === 0) return
    
    const selectedData = filteredRecords.filter(record => selectedRecords.has(record.id))
    
    // 准备Excel数据
    const excelData = selectedData.map(record => {
      const baseData: Record<string, string> = {
        '员工姓名': record.employeeName,
        '员工工号': record.employeeId,
        '所在城市': getCityDisplayName(record.city),
        '产假开始日期': formatDate(record.startDate),
        '产假结束日期': formatDate(record.endDate),
        '产假总天数': `${record.totalDays}天`,
        '生育类型': getBirthTypeText(record.vacationData?.birthType),
        '生育方式': getDeliveryMethodText(record.vacationData?.deliveryMethod),
        '晚育/生育/奖励假': record.vacationData?.hasExtendedDays ? '是' : '否',
        '计算时间': formatDate(record.calculatedAt)
      }
      
      // 如果有怀孕周数，添加该字段
      if (record.vacationData?.pregnancyDuration) {
        baseData['怀孕周数'] = `${record.vacationData.pregnancyDuration}周`
      }
      
      // 如果是津贴记录，添加津贴相关数据
      if (record.recordType === 'allowance') {
        // 判断是否为发薪城市（深圳）
        const isPayrollCity = record.city === 'SZ' || record.cityName?.includes('深圳')
        
        if (record.allowanceData?.governmentSubsidy) {
          baseData['政府津贴'] = formatCurrency(record.allowanceData.governmentSubsidy)
        }
        
        if (record.allowanceData?.monthlyAverageSalary) {
          baseData['应享受补贴金额'] = formatCurrency(record.allowanceData.monthlyAverageSalary)
        }
        
        if (isPayrollCity && record.allowanceData?.socialInsuranceBase) {
          baseData['实发薪资'] = formatCurrency(record.allowanceData.socialInsuranceBase)
        }
        
        baseData['津贴天数'] = `${record.totalDays}天`
        baseData['津贴补差'] = formatCurrency(record.companyCompensation)
        
        // 不发薪城市显示返还金额
        if (!isPayrollCity && record.employeeCompensation !== undefined) {
          baseData['返还金额'] = formatCurrency(record.employeeCompensation)
        }
        
        // 添加提交核定表时间和提交产假时间
        baseData['提交核定表时间'] = record.receivedTime || ''
        baseData['提交产假时间'] = (record.allowanceData?.maternitySubmissionDate as string) || ''

        // 追加 allowanceData 全字段（中文映射），在保持现有列的基础上，避免重复
        const ad = record.allowanceData || {}
        const labelMap: Record<string, string> = {
          governmentSubsidy: '政府津贴',
          monthlyAverageSalary: '应享受补贴金额',
          paidMaternityWage: '产假期间发放工资',
          socialInsuranceBase: '实发薪资',
          paidSalary: '员工基本工资',
          avgSalary12Months: '12个月平均工资',
          declaredSalary: '单位申报上年度月均',
          monthlyBaseSalary: '基本工资（跨四月）',
          adjustedMonthlyBaseSalary: '调整后基本工资',
          employeeInsurance: '五险一金（个人）',
          supplementaryFund: '补充公积金（个人）',
          unionFee: '工会费（个人）',
          espp: 'ESPP',
          spotOn: 'spot on',
          flexibleBenefit: '弹性福利',
          otherDeductions: '其他扣减',
          employerESPP: '月度ESPP',
          employerSupplementaryFund: '月度补充公积金',
          personalIncomeTax: '月度工会费',
          crossApril: '是否跨四月',
          crossJuly: '是否跨七月',
          insuranceBefore: '七月前社保基数',
          insuranceAfter: '七月后社保基数',
          receivedTime: '提交核定表时间',
          submissionTime: '提交产假时间',
          maternitySubmissionDate: '产假提交日期'
        }
        const moneyKeys = new Set([
          'governmentSubsidy', 'monthlyAverageSalary', 'paidMaternityWage', 'socialInsuranceBase',
          'paidSalary', 'avgSalary12Months', 'declaredSalary', 'monthlyBaseSalary', 'adjustedMonthlyBaseSalary',
          'employeeInsurance', 'supplementaryFund', 'unionFee', 'espp', 'spotOn', 'flexibleBenefit',
          'otherDeductions', 'employerESPP', 'employerSupplementaryFund', 'personalIncomeTax',
          'insuranceBefore', 'insuranceAfter'
        ])

        // 需要排除不导出的字段
        const skipKeys = new Set(['city', 'governmentAmount', 'isBankEmployee', 'advanceInputMethod'])

        Object.keys(ad).forEach((key) => {
          if (key === 'customInputs' || key === 'customDeductions') return
          if (skipKeys.has(key)) return
          const label = labelMap[key] || key
          // 若现有列已包含（如政府津贴/应享受补贴金额/实发薪资等），则跳过，避免重复
          if (baseData[label] !== undefined) return
          const val = (ad as Record<string, unknown>)[key]
          let out = ''
          if (typeof val === 'boolean') {
            out = val ? '是' : '否'
          } else if (val === null || val === undefined || val === '') {
            out = ''
          } else if (moneyKeys.has(key)) {
            const num = typeof val === 'number' ? val : parseFloat(String(val))
            out = isNaN(num) ? String(val) : formatCurrency(num)
          } else {
            out = String(val)
          }
          baseData[label] = out
        })

        // 展平自定义增项/减项
        const formatCustomList = (arr: Array<{label: string, value: string}> | unknown) => {
          if (!Array.isArray(arr) || arr.length === 0) return ''
          return (arr as Array<{label: string, value: string}>)
            .map(it => `${it.label}=${formatCurrency(Number(it.value) || 0)}`)
            .join('; ')
        }
        const customAdds = formatCustomList((ad as any).customInputs)
        const customSubs = formatCustomList((ad as any).customDeductions)
        if (customAdds) baseData['自定义增项'] = customAdds
        if (customSubs) baseData['自定义减项'] = customSubs
      }
      
      return baseData
    })
    
    // 创建工作簿
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(excelData)
    
    // 设置列宽
    const colWidths = [
      { wch: 12 }, // 员工姓名
      { wch: 15 }, // 员工工号
      { wch: 12 }, // 所在城市
      { wch: 15 }, // 产假开始日期
      { wch: 15 }, // 产假结束日期
      { wch: 12 }, // 产假总天数
      { wch: 12 }, // 生育类型
      { wch: 12 }, // 分娩方式
      { wch: 18 }, // 晚育/生育/奖励假
      { wch: 15 }, // 计算时间
      { wch: 12 }, // 怀孕周数
      { wch: 15 }, // 政府津贴
      { wch: 18 }, // 应享受补贴金额
      { wch: 15 }, // 实发薪资
      { wch: 12 }, // 津贴天数
      { wch: 15 }, // 津贴补差
      { wch: 15 }, // 返还金额
      { wch: 15 }, // 发放时间
      { wch: 18 }  // 提交产假时间
    ]
    ws['!cols'] = colWidths
    
    // 添加工作表
    XLSX.utils.book_append_sheet(wb, ws, '历史记录')
    
    // 生成文件名
    const now = new Date()
    const timestamp = now.toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_')
    const filename = `产假津贴历史记录_${timestamp}.xlsx`
    
    // 导出文件
    XLSX.writeFile(wb, filename)
    
    console.log(`成功导出 ${selectedRecords.size} 条记录到 ${filename}`)
  }

  const getCityCodeByName = (cityName: string) => {
    // 城市名称到代码的映射
    const cityNameToCodeMap: { [key: string]: string } = {
      '北京': 'BJ',
      '佛山': 'FS', 
      '广州': 'GZ',
      '深圳': 'SZ',
      '珠海': 'ZH',
      '武汉': 'WH',
      '苏州（园区）': 'SZ2',
      '成都': 'CD',
      '重庆': 'CQ',
      '天津': 'TJ',
      '厦门': 'XM',
      '上海': 'SH',
      '青岛': 'QD',
      '绍兴': 'SX'
    }
    
    // 如果传入的是城市代码，直接返回
    if (cityNameToCodeMap[cityName]) {
      return cityNameToCodeMap[cityName]
    }
    
    // 如果传入的是城市代码（小写），转换为大写
    const city = cities.find(c => c.value === cityName)
    return city ? city.value.toUpperCase() : 'SH'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN')
  }

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return '-'
    return `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`
  }
  
  const getBirthTypeText = (birthType?: string) => {
    switch (birthType) {
      case 'single': return '单胎'
      case 'twins': return '双胞胎'
      case 'triplets': return '三胞胎及以上'
      default: return '单胎'
    }
  }

  const getDeliveryMethodText = (method?: string) => {
    switch (method) {
      case 'normal': return '顺产'
      case 'difficult': return '难产'
      case 'miscarriage': return '流产'
      default: return '顺产'
    }
  }

  const getCityDisplayName = (cityCode: string) => {
    // 从动态加载的城市列表中查找
    const city = cities.find(c => c.value === cityCode)
    if (city) {
      return city.label
    }
    
    // 如果没找到，使用备用映射
    const fallbackCityMap: { [key: string]: string } = {
      'BJ': '北京',
      'FS': '佛山', 
      'GZ': '广州',
      'SZ': '深圳',
      'ZH': '珠海',
      'WH': '武汉',
      'SZ2': '苏州（园区）',
      'SU': '苏州',
      'CD': '成都',
      'CQ': '重庆',
      'TJ': '天津',
      'XM': '厦门',
      'SH': '上海',
      'QD': '青岛',
      'SX': '绍兴'
    }
    
    return fallbackCityMap[cityCode] || cityCode
  }

  // 从localStorage加载历史记录
  useEffect(() => {
    loadHistoryRecords()
    loadCities() // 加载城市列表
  }, [loadHistoryRecords])

  // 筛选记录
  useEffect(() => {
    filterRecords()
  }, [filterRecords])
  
  // 计算分页数据
  const totalPages = Math.ceil(filteredRecords.length / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const currentPageRecords = filteredRecords.slice(startIndex, endIndex)
  
  // 处理排序切换
  const handleSortToggle = (field: 'calculatedAt' | 'receivedTime' | 'submissionTime') => {
    if (sortField === field) {
      // 如果点击的是当前排序字段，切换排序方向
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')
    } else {
      // 如果点击的是不同字段，设置新字段并默认为降序
      setSortField(field)
      setSortOrder('desc')
    }
  }

  // 创建具体的排序处理函数
  const handleCalculatedAtSort = () => handleSortToggle('calculatedAt')
  const handleReceivedTimeSort = () => handleSortToggle('receivedTime')
  const handleSubmissionTimeSort = () => handleSortToggle('submissionTime')
  
  // 处理页码变更
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      // 不清空选中状态，保持跨页选中
      // 但需要更新全选框状态以反映新页面的选中情况
    }
  }
  
  // 监听页面变化，更新全选框状态
  React.useEffect(() => {
    const currentPageIds = currentPageRecords.map(r => r.id)
    const currentPageAllSelected = currentPageIds.length > 0 && 
      currentPageIds.every(id => selectedRecords.has(id))
    setSelectAll(currentPageAllSelected)
  }, [currentPage, currentPageRecords, selectedRecords])

  return (
    <div>
      <Header />
      <Navigation />
      
      <div className="container">
        <header className="header">
          <div className="header-content">
            <i className="fas fa-history header-icon" />
            <h1>历史记录查询</h1>
            <p className="subtitle">查询和管理已计算的产假津贴记录</p>
          </div>
        </header>

        <main className="main-content">
          <div className="calculator-card">
            <div className="calculator-form">
              {/* 搜索条件 */}
              <div className="search-filters">
                <div className="filter-row">
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-user-search" />
                      姓名或工号
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="请输入员工姓名或工号进行搜索"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-map-marker-alt" />
                      所在城市
                    </label>
                    <select
                      className="form-input"
                      value={selectedCity}
                      onChange={(e) => setSelectedCity(e.target.value)}
                    >
                      {cityOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 搜索结果统计 */}
              <div className="search-summary">
                <div className="summary-item">
                  <i className="fas fa-list-ul" />
                  <span>共找到 {filteredRecords.length} 条记录</span>
                </div>
                {selectedRecords.size > 0 && (
                  <div className="batch-actions">
                    <span className="selected-count">已选中 {selectedRecords.size} 条</span>
                    <button 
                      className="batch-delete-btn"
                      onClick={handleBatchDelete}
                    >
                      <i className="fas fa-trash" /> 批量删除
                    </button>
                    {isHrEnabled() && (
                      <button 
                        className="batch-export-btn"
                        onClick={handleBatchExport}
                      >
                        <i className="fas fa-download" /> 批量导出
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 历史记录表格 */}
          <div className="history-table-container">
            <div className="calculator-card">
              <div className="table-responsive">
                {filteredRecords.length > 0 ? (
                  <table className={`history-table ${!employeeCompensationCities.some(city => 
                    currentPageRecords.some(record => record.city === city)
                  ) ? 'no-employee-compensation' : ''}`}>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            title="全选/取消全选"
                          />
                        </th>
                        <th>姓名</th>
                        <th>工号</th>
                        <th>城市</th>
                        <th>产假时间</th>
                        <th>公司需补差</th>
                        {employeeCompensationCities.some(city => 
                          currentPageRecords.some(record => record.city === city)
                        ) && <th>员工需返还</th>}
                        <th 
                          className="sortable-header"
                          onClick={handleReceivedTimeSort}
                          title={`点击按政府补贴发放时间${sortField === 'receivedTime' && sortOrder === 'desc' ? '正序' : '倒序'}排列`}
                        >
                          {sortField === 'receivedTime' && <i className={`fas fa-sort-amount-${sortOrder === 'desc' ? 'down' : 'up'}`} />}
                          提交核定表时间
                        </th>
                        <th 
                          className="sortable-header"
                          onClick={handleSubmissionTimeSort}
                          title={`点击按提交核定表时间${sortField === 'submissionTime' && sortOrder === 'desc' ? '正序' : '倒序'}排列`}
                        >
                          {sortField === 'submissionTime' && <i className={`fas fa-sort-amount-${sortOrder === 'desc' ? 'down' : 'up'}`} />}
                          提交产假时间
                        </th>
                        <th 
                          className="sortable-header"
                          onClick={handleCalculatedAtSort}
                          title={`点击按计算时间${sortField === 'calculatedAt' && sortOrder === 'desc' ? '正序' : '倒序'}排列`}
                        >
                          {sortField === 'calculatedAt' && <i className={`fas fa-sort-amount-${sortOrder === 'desc' ? 'down' : 'up'}`} />}
                          计算时间
                        </th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPageRecords.map(record => (
                        <tr 
                          key={record.id} 
                          className="history-row"
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedRecords.has(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </td>
                          <td className="employee-name">{record.employeeName}</td>
                          <td className="employee-id">{record.employeeId}</td>
                          <td className="city-name">{getCityDisplayName(record.city)}</td>
                          <td className="vacation-period">
                            {formatDate(record.startDate)} - {formatDate(record.endDate)}
                            <br />
                            <small className="vacation-days">({record.totalDays}天)</small>
                          </td>
                          <td className="company-compensation">
                            {record.recordType === 'allowance' 
                              ? formatCurrency(record.companyCompensation || 0)
                              : '-'
                            }
                          </td>
                          {employeeCompensationCities.some(city => 
                            currentPageRecords.some(r => r.city === city)
                          ) && (
                            <td className="employee-compensation">
                              {employeeCompensationCities.includes(record.city) && record.recordType === 'allowance' && record.employeeCompensation !== undefined
                                ? formatCurrency(record.employeeCompensation)
                                : '-'
                              }
                            </td>
                          )}
                          <td className="received-time">
                            {record.receivedTime || '-'}
                          </td>
                          <td className="submission-time">
                            {(record.allowanceData?.maternitySubmissionDate as string) || '-'}
                          </td>
                          <td className="calculated-time">
                            {formatDate(record.calculatedAt)}
                          </td>
                          <td className="actions">
                            <button 
                              className="view-btn"
                              onClick={() => handleViewDetails(record)}
                              title="查看详情"
                            >
                              <i className="fas fa-eye" /> 详情
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-records">
                    <i className="fas fa-inbox" />
                    <h3>暂无记录</h3>
                    <p>没有找到符合条件的历史记录</p>
                  </div>
                )}
              </div>
              
              {/* 分页控件和本地缓存提示 */}
              <div className="pagination-wrapper">
                {/* 本地缓存提示 */}
                <div className="cache-notice">
                  <i className="fas fa-shield-alt"></i>
                  <div className="cache-notice-content">
                    <strong>数据安全提示：</strong>
                    <span>所有数据仅存储于本地浏览器，不会上传至任何服务器。建议定期导出备份，防止数据丢失。</span>
                  </div>
                </div>

                {/* 分页控件 */}
                {filteredRecords.length > 0 && totalPages > 1 && (
                  <div className="pagination-container">
                    <div className="pagination">
                      <button 
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-chevron-left" />
                        上一页
                      </button>
                      
                      <div className="page-numbers">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button 
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        下一页
                        <i className="fas fa-chevron-right" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <footer className="footer">
          <p>© 2025 Twins HR产假津贴管理系统 | 仅供内部使用</p>
        </footer>
      </div>

      {/* 详情弹窗 */}
      <RecordDetailDialog
        record={selectedRecord}
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onRecalculate={handleRecordClick}
      />
    </div>
  )
}

export default HistoryRecords
